// form
import { Controller, useFormContext } from "react-hook-form";
// @mui
import { Box, FormHelperText, InputLabel, Stack } from "@mui/material";
// type
import { Button, useTheme } from "@mui/material";
import useLocales from "src/hooks/useLocales";
import MyErrorHelperText from "../denseForm/ErrorHelperText";
import {
  UploadAvatar,
  UploadMultiFile,
  UploadMultiFileProps,
  UploadProps,
  UploadSingleFile,
} from "../upload";
import { SingleUploadDenseView } from "../upload/dense/SingleUploadDense";

// ----------------------------------------------------------------------

interface Props extends Omit<UploadProps, "file"> {
  name: string;
  fileName?: string;
  dense?: boolean;
  handleDelete?: () => void;
  label?: string;
  isButtonDisabled?: boolean;
  fileUploadMessage?: string;
  singleUpload?: boolean;
  isBlockContentV2Enabled?: boolean;
  contentFooter?: string;
  disableMinHeight?: boolean;
}

export function RHFUploadAvatar({ name, ...other }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && !field.value;

        return (
          <div>
            <UploadAvatar error={checkError} {...other} file={field.value} />
            {checkError && (
              <FormHelperText error sx={{ px: 2, textAlign: "center" }}>
                {error.message}
              </FormHelperText>
            )}
          </div>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUploadSingleFile({
  name,
  dense = false,
  label,
  handleDelete,
  isButtonDisabled,
  fileName,
  fileUploadMessage,
  singleUpload,
  isBlockContentV2Enabled,
  contentFooter,
  disableMinHeight = false, 
  ...other
}: Props) {
  const { control, setValue } = useFormContext();

  const theme = useTheme();
  const { translate } = useLocales();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && !field.value;

        if (dense) {
          return (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 0.5,
                  minHeight: disableMinHeight ? "auto" : "40px", 
                  pb: disableMinHeight ? 0 : 0.5,
                }}
              >
                {label && (
                  <InputLabel
                    htmlFor="demo-customized-textbox"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: theme.palette.action.active,
                      pb: 0.5,
                    }}
                  >
                    {label}
                  </InputLabel>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: "flex-end",
                    alignItems: "center",
                    pb: -2,
                  }}
                >
                  {typeof field.value === "string" &&
                  field.value?.length > 0 &&
                  Boolean(singleUpload) === false ? (
                    <Button
                      size="small"
                      sx={{
                        justifyContent: "center",
                        pr: 0,
                      }}
                      onClick={() => {
                        window.open(field?.value, "_blank");
                      }}
                      disabled={isButtonDisabled}
                    >
                      {translate("View")}
                    </Button>
                  ) : field.value && field.value.preview ? (
                    <Button
                      size="small"
                      sx={{
                        justifyContent: "center",
                        pr: 0,
                      }}
                      onClick={() => {
                        window.open(field?.value.preview, "_blank");
                      }}
                      disabled={isButtonDisabled}
                    >
                      {translate("View")}
                    </Button>
                  ) : null}
                  {field.value && handleDelete && (
                    <Button
                      size="small"
                      sx={{
                        justifyContent: "center",
                        pr: 0,
                      }}
                      onClick={handleDelete}
                      disabled={isButtonDisabled}
                    >
                      {translate("remove")}
                    </Button>
                  )}
                </Stack>
              </Box>
              <SingleUploadDenseView
                file={field.value}
                error={checkError}
                fileName={fileName || field?.value?.name}
                helperText={
                  checkError && (
                    <MyErrorHelperText
                      error={!!error}
                      helperText={error?.message}
                    />
                  )
                }
                fileUploadMessage={fileUploadMessage}
                singleUpload={singleUpload}
                {...other}
              />
            </>
          );
        }
        return (
          <UploadSingleFile
            file={field.value}
            error={checkError}
            fileName={fileName}
            helperText={
              checkError && (
                <FormHelperText error>{error.message}</FormHelperText>
              )
            }
            contentFooter={contentFooter}
            isBlockContentV2Enabled={isBlockContentV2Enabled}
            {...other}
          />
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

interface RHFUploadMultiFileProps extends Omit<UploadMultiFileProps, "files"> {
  name: string;
}

export function RHFUploadMultiFile({
  name,
  ...other
}: RHFUploadMultiFileProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && field.value?.length === 0;

        return (
          <UploadMultiFile
            accept="image/*"
            files={field.value}
            error={checkError}
            helperText={
              checkError && (
                <FormHelperText error sx={{ px: 2 }}>
                  {error?.message}
                </FormHelperText>
              )
            }
            {...other}
          />
        );
      }}
    />
  );
}
